import { useState } from 'react';
import { X } from 'lucide-react';
import FormField from '../ui/FormField';

/**
 * Modal para criar novo agente (dados mockados).
 * Ponto de integração: enviar payload para POST /api/agents.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {(payload: { internalName: string, publicName: string, teamId?: string, prompt?: string }) => void} props.onCreate
 * @param {{ id: string, name: string }[]} [props.teams] - lista de equipes para role
 */
export default function CreateAgentModal({ open, onClose, onCreate, teams = [] }) {
  const [internalName, setInternalName] = useState('');
  const [publicName, setPublicName] = useState('');
  const [teamId, setTeamId] = useState(teams[0]?.id ?? '');
  const [prompt, setPrompt] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const name = (internalName || publicName || 'Novo Agente').trim();
    onCreate({
      internalName: internalName.trim() || name,
      publicName: publicName.trim() || name,
      teamId: teamId || undefined,
      prompt: prompt.trim() || '',
    });
    setInternalName('');
    setPublicName('');
    setTeamId(teams[0]?.id ?? '');
    setPrompt('');
    onClose();
  };

  const handleClose = () => {
    setInternalName('');
    setPublicName('');
    setTeamId(teams[0]?.id ?? '');
    setPrompt('');
    onClose();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-agent-title"
    >
      <div className="bg-appSurface rounded-3xl border border-appSurfaceBorder shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-appSurfaceBorder">
          <h2 id="create-agent-title" className="text-lg font-bold text-appText">
            Criar Agente
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 rounded-lg text-appTextMuted hover:bg-appSurfaceBorder/30 hover:text-appText transition-colors"
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <FormField label="Nome interno" id="create-internal">
            <input
              id="create-internal"
              type="text"
              value={internalName}
              onChange={(e) => setInternalName(e.target.value)}
              placeholder="Ex: Agente de Vendas 01"
              className="w-full bg-appSurfaceBorder/20 border border-appSurfaceBorder rounded-xl p-3 text-sm font-medium text-appText focus:ring-2 focus:ring-appPrimary outline-none"
            />
          </FormField>
          <FormField label="Nome público" id="create-public">
            <input
              id="create-public"
              type="text"
              value={publicName}
              onChange={(e) => setPublicName(e.target.value)}
              placeholder="Ex: Juliana Castro"
              className="w-full bg-appSurfaceBorder/20 border border-appSurfaceBorder rounded-xl p-3 text-sm font-medium text-appText focus:ring-2 focus:ring-appPrimary outline-none"
            />
          </FormField>
          {teams.length > 0 && (
            <FormField label="Equipe / Role" id="create-team">
              <select
                id="create-team"
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
                className="w-full bg-appSurfaceBorder/20 border border-appSurfaceBorder rounded-xl p-3 text-sm font-medium text-appText focus:ring-2 focus:ring-appPrimary outline-none"
              >
                <option value="">—</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </FormField>
          )}
          <FormField label="Instruções principais (opcional)" id="create-prompt">
            <textarea
              id="create-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Prompt inicial do agente..."
              rows={3}
              className="w-full bg-appSurfaceBorder/20 border border-appSurfaceBorder rounded-xl p-3 text-sm text-appText focus:ring-2 focus:ring-appPrimary outline-none resize-none"
            />
          </FormField>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 py-2.5 rounded-xl font-bold text-sm border border-appSurfaceBorder text-appText hover:bg-appSurfaceBorder/20 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 py-2.5 bg-appPrimary text-white rounded-xl font-bold text-sm hover:opacity-90 transition-all shadow-lg"
            >
              Criar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
